import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowRight, 
  Shield, 
  TrendingUp, 
  Globe, 
  CheckCircle, 
  MessageCircle,
  ChevronDown,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import '@/styles/Home.css';

const Home = () => {
  const [activeSection, setActiveSection] = useState('hero');
 

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'services', 'value', 'industries', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

 

  return (
    <div className="home-container">
      {/* Navigation Header */}
      <header className="nav-header">
        <div className="nav-content">
          <div className="logo-section">
            <div className="logo-text">Moon Global Trade</div>
          </div>
          <nav className="nav-links">
            <button 
              onClick={() => scrollToSection('about')} 
              className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('services')} 
              className={`nav-link ${activeSection === 'services' ? 'active' : ''}`}
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('value')} 
              className={`nav-link ${activeSection === 'value' ? 'active' : ''}`}
            >
              Value
            </button>
            <button 
              onClick={() => scrollToSection('industries')} 
              className={`nav-link ${activeSection === 'industries' ? 'active' : ''}`}
            >
              Industries
            </button>
            <button 
              onClick={() => scrollToSection('contact')} 
              className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
            >
              Contact
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Principal Trading & Risk Management
            </h1>
            <p className="hero-subtitle">
              Executing fully hedged, back-to-back commodity transactions with transparency, 
              security and credit management for global markets.
            </p>
            <div className="hero-actions">
              <Button 
                onClick={() => scrollToSection('services')}
                className="btn-primary"
              >
                Explore Services
                <ArrowRight className="icon-right" />
              </Button>
              <Button 
                onClick={() => scrollToSection('contact')}
                variant="outline"
                className="btn-secondary"
              >
                Get in Touch
              </Button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-card">
              <Globe className="hero-icon" />
              <div className="hero-card-content">
                <h3>Global Reach</h3>
                <p>Connecting producers, processors and end-users worldwide</p>
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => scrollToSection('about')}
          className="scroll-indicator"
          aria-label="Scroll to next section"
        >
          <ChevronDown className="scroll-icon" />
        </button>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">About Moon Global Trade</h2>
            <p className="section-description">
              A principal trading and risk-management firm specializing in physical commodity 
              and raw material transactions
            </p>
          </div>
          <div className="about-content">
            <Card className="about-card">
              <CardContent className="about-card-content">
                <p className="about-text">
                  Moon Global Trade Pte Ltd operates as a principal in commodity trading, 
                  executing fully hedged or back-to-back purchase and sale transactions. 
                  We deliver secure, transparent and credit-managed trade flows across energy, 
                  metals, agricultural commodities and industrial raw materials.
                </p>
                <p className="about-text">
                  Our integrated approach combines physical trading, hedging and financing 
                  solutions to optimize capital allocation and risk management for producers, 
                  processors, trading houses and institutional counterparties worldwide.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Core Services</h2>
            <p className="section-description">
              Comprehensive commodity trading and risk management solutions
            </p>
          </div>
          <div className="services-grid">
            <Card className="service-card">
              <CardContent className="service-content">
                <div className="service-icon-wrapper">
                  <TrendingUp className="service-icon" />
                </div>
                <h3 className="service-title">Principal Physical Trading</h3>
                <p className="service-description">
                  Execution of fully hedged or back-to-back purchase and sale transactions 
                  across energy, metals, agricultural commodities and industrial raw materials. 
                  End-to-end deal management from contracting to delivery.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="service-content">
                <div className="service-icon-wrapper">
                  <Globe className="service-icon" />
                </div>
                <h3 className="service-title">Brokerage & Advisory</h3>
                <p className="service-description">
                  Selective brokerage and advisory services supporting clients on market access, 
                  transactional structuring and optimization of commodity flows.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="service-content">
                <div className="service-icon-wrapper">
                  <Shield className="service-icon" />
                </div>
                <h3 className="service-title">Hedging & Risk Structuring</h3>
                <p className="service-description">
                  Sourcing and structuring tailored price, FX and interest-rate hedging strategies 
                  using swaps, forwards, options and bespoke OTC solutions to mitigate market and 
                  financing risk.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="service-content">
                <div className="service-icon-wrapper">
                  <TrendingUp className="service-icon" />
                </div>
                <h3 className="service-title">Financing Solutions</h3>
                <p className="service-description">
                  Arrangement and structuring of trade and commodity financing, including 
                  pre-payment, receivables financing, warehouse and structured trade finance facilities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section id="value" className="value-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Value Proposition</h2>
            <p className="section-description">
              Why leading counterparties choose Moon Global Trade
            </p>
          </div>
          <div className="value-grid">
            <div className="value-item">
              <CheckCircle className="value-icon" />
              <div className="value-content">
                <h3 className="value-title">Principal-Led Execution</h3>
                <p className="value-text">
                  Counterpart certainty and streamlined transaction settlement through direct 
                  principal participation.
                </p>
              </div>
            </div>

            <div className="value-item">
              <CheckCircle className="value-icon" />
              <div className="value-content">
                <h3 className="value-title">Integrated Approach</h3>
                <p className="value-text">
                  Combining physical trading, hedging and financing for efficient capital and 
                  risk allocation.
                </p>
              </div>
            </div>

            <div className="value-item">
              <CheckCircle className="value-icon" />
              <div className="value-content">
                <h3 className="value-title">Deep Market Access</h3>
                <p className="value-text">
                  Extensive network of counterparties to source supply and offtake with 
                  competitive terms.
                </p>
              </div>
            </div>

            <div className="value-item">
              <CheckCircle className="value-icon" />
              <div className="value-content">
                <h3 className="value-title">Custom Structuring</h3>
                <p className="value-text">
                  Aligning commercial objectives with prudent risk management and regulatory compliance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="industries-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Industry Focus</h2>
            <p className="section-description">
              Serving diverse commodity sectors with tailored solutions
            </p>
          </div>
          <div className="industries-content">
            <Card className="industry-card">
              <CardContent className="industry-content">
                <p className="industry-text">
                  We serve commodity producers, processors, manufacturers, trading houses, 
                  and institutional counterparties across <strong>energy</strong>, <strong>metals</strong>, 
                  <strong> agriculture</strong> and <strong>industrial raw material</strong> sectors, 
                  adapting solutions to both spot and longer-term offtake needs.
                </p>
                <Separator className="my-6" />
                <div className="compliance-section">
                  <h3 className="compliance-title">Governance & Compliance</h3>
                  <p className="compliance-text">
                    Moon Global maintains robust credit vetting, compliance and trade-documentation 
                    practices. We adhere to applicable market, trade-finance and AML/KYC standards 
                    and operate with transparent reporting and risk-control frameworks.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Get in Touch</h2>
            <p className="section-description">
              Connect with our team to discuss your commodity trading needs
            </p>
          </div>
          <div className="contact-grid">
            <Card className="contact-card">
              <CardContent className="contact-content">
                <div className="contact-item">
                  <Mail className="contact-icon" />
                  <div>
                    <h4 className="contact-label">Email</h4>
                    <p className="contact-value">info@moonglobaltrade.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="contact-card">
              <CardContent className="contact-content">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <div>
                    <h4 className="contact-label">Phone</h4>
                    <p className="contact-value">+65 9123 4567</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="contact-card">
              <CardContent className="contact-content">
                <div className="contact-item">
                  <MapPin className="contact-icon" />
                  <div>
                    <h4 className="contact-label">Location</h4>
                    <p className="contact-value">Singapore</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">Moon Global Trade Pte Ltd</h3>
            <p className="footer-text">
              Principal trading and risk management for global commodity markets.
            </p>
          </div>
          <div className="footer-section">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Moon Global Trade Pte Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

     
    </div>
  );
};

export default Home;
